import "./AccountSettings.css";
import ftmlogo from "../../assets/images/fresh-thyme-market-logo.png";
import React, {useEffect, useMemo, useState, useCallback} from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {Row, Col, Navbar, Nav} from "react-bootstrap";
import {PreferredStoreLocation} from "../../pages/PreferredStoreLocationPage";
import {ProfilePage} from "../../pages/ProfilePage";
import {AddressesPage} from "../../pages/AddressesPage";
import {userProfileService} from "../../service/userProfile/userProfile.service";
import {getStoreLocationService} from "../../service/storeLocator/storeLocator.service";
import {AddPreferredLocationModal} from "../modals/AddPreferredLocationModal";
import {PasswordAndSecurity} from "../../pages/PasswordnSecurityPage";
import {Container} from "@mui/material";
import LoyaltyPage from "../../pages/LoyaltyPage";
import {SubscriptionsPage} from "../../pages/SubscriptionsPage";
import {useAuth0} from "@auth0/auth0-react";
import {useLocation} from "react-router-dom";

const defaultRedirectUrl = process.env.REACT_APP_STS_REDIRECT_LOGIN_LINK;

export const AccountSettings = (props) => {

    const auth = useAuth0();

    const [customerInfo, setCustomerInfo] = useState(null);
    const [store, setStore] = useState(null);
    const [storeList, setStoreList] = useState([]);
    const [showPreferredStoreModal, setShowPreferredStoreModal] = useState(false);
    const getHash = () => window.sessionStorage.getItem("tab") || "profile";
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(getHash());
    const [alert, setAlert] = useState(null);

    const [isCustomerInfoFetched, setIsCustomerInfoFetched] = useState(false);

    const handleClosePreferredStoreModal = useCallback((newStore) => {
        if (newStore) setStore(newStore);
        setShowPreferredStoreModal(false);
    }, []);

    const handleShowPreferredStoreModal = useCallback(() => {
        setShowPreferredStoreModal(true);
    }, []);

    useEffect(() => {
        if (location?.state?.action === "unenroll-mfa") {
            setAlert({
                id: Date.now(),
                type: "danger",
                msg: "MFA has been unenrolled.",
            });
        } else if (props?.syncMsg) {
            setAlert({
                id: Date.now(),
                ...props.syncMsg
            });
        }
    }, [location.state, props.syncMsg]);

    useEffect(() => {
        let isMounted = true;

        const fetchCustomerData = async () => {
            if (!auth?.user) return;

            try {
                if (!isMounted) return;
                const data = await userProfileService.customerInfo();
                if (!data || !isMounted) return;

                setCustomerInfo(data);
                setIsCustomerInfoFetched(true);
                const storeDetails = await getStoreLocationService.getStoreLocator();
                if (!storeDetails?.Stores || !isMounted) return;

                let defaultStore = {email: data.email};

                if (data.storeNumber) {
                    const foundStore = storeDetails.Stores.find(
                        (s) => s.storeNumber === Number(data.storeNumber)
                    );

                    if (foundStore) {
                        defaultStore = {...foundStore, email: data.email};
                    }
                } else {
                    handleShowPreferredStoreModal();
                }

                setStore(defaultStore);
                setStoreList(storeDetails.Stores.filter((s) => s.ecomName));

            } catch (error) {
                console.error("Error fetching customer info or store data:", error);
            }
        };

        fetchCustomerData();

        return () => {
            isMounted = false;
        };
    }, [auth, auth?.user, handleShowPreferredStoreModal]);

    useEffect(() => {
        const onHashChange = () => {
            setActiveTab(getHash());
        };
        window.addEventListener("hashchange", onHashChange);
        setActiveTab(getHash());

        return () => {
            window.removeEventListener("hashchange", onHashChange);
        };
    }, []);

    const updateCustomerInfo = useCallback((userProfile) => {
        setCustomerInfo(userProfile);
    }, []);

    const switchTab = (tabName) => {
        setActiveTab(tabName)
        window.sessionStorage.setItem("tab", tabName);
    }
    const preferredStoreDetails = useMemo(
        () => ({
            ...props,
            show: showPreferredStoreModal,
            handleClose: handleClosePreferredStoreModal,
            oldStore: store,
            storeList,
            auth,
        }),
        [
            props,
            showPreferredStoreModal,
            handleClosePreferredStoreModal,
            store,
            storeList,
            auth,
        ]
    );

    const mergedProps = useMemo(
        () => ({
            ...props,
            ...customerInfo,
            updateCustomerInfo,
            auth,
            isCustomerInfoFetched,
            alert, setAlert
        }),
        [props, customerInfo, updateCustomerInfo, auth, isCustomerInfoFetched, alert]
    );

    const loyaltyProps = useMemo(
        () => ({
            ...customerInfo,
            auth,
        }),
        [customerInfo, auth]
    );

    const redirectToWynshop = useCallback(() => {
        try {
            sessionStorage.clear();
        } catch (e) {
            console.error("Error clearing session:", e);
        } finally {
            window.location.href = defaultRedirectUrl;
        }
    }, []);

    const renderTabContent = () => {
        switch (activeTab) {
            case "profile":
                return <ProfilePage {...mergedProps} />;
            case "passwordnSecurity":
                return <PasswordAndSecurity {...mergedProps} />;
            case "address":
                return <AddressesPage {...mergedProps} />;
            case "preferredStoreLocation":
                return <PreferredStoreLocation {...preferredStoreDetails} />;
            case "loyalty":
                return <LoyaltyPage {...loyaltyProps} />;
            case "subscriptions":
                return <SubscriptionsPage />;
            default:
                return null;
        }
    };

    if (!auth?.user) {
        return <div className="text-center m-6-auto">Loading now...</div>;
    }

    const handleLogout = async () => {
        try {
            await auth.logout({
                logoutParams: {
                    returnTo: window.location.href,
                },
            });
        } catch (err) {
            console.error(err);
        }
    };

    return (

        <div className="text-center m-6-auto" style={{marginTop: "20px"}}>

            <div>
                <a
                    href={process.env.REACT_APP_REDIRECT_URI}
                    className="logo-link"
                    aria-label="Go to home page"
                >
                    <img src={ftmlogo} alt="Fresh Thyme Market logo"/>
                </a>
            </div>
            <div style={{marginTop: "20px"}}>
                <span className="number">
                    Hello {customerInfo?.firstName}
                </span>
            </div>

            <AddPreferredLocationModal {...preferredStoreDetails} />

            <Navbar expand="md" className="mb-3 nav-profile">
                <Container>
                    <Navbar.Toggle/>
                    <Navbar.Collapse>
                        <Nav activeKey={activeTab} onSelect={switchTab} className="flex-column flex-md-row w-100">
                            <Nav.Link eventKey="profile" className="flex-fill text-center">Profile</Nav.Link>
                            <Nav.Link eventKey="passwordnSecurity" className="flex-fill text-center">Security</Nav.Link>
                            <Nav.Link eventKey="address" className="flex-fill text-center">Address</Nav.Link>
                            <Nav.Link eventKey="preferredStoreLocation" className="flex-fill text-center">Preferred
                                Store</Nav.Link>
                            <Nav.Link eventKey="loyalty" className="flex-fill text-center">Loyalty</Nav.Link>
                            <Nav.Link eventKey="subscriptions" className="flex-fill text-center">Preferences</Nav.Link>
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>

            <Row>
                <Col>{renderTabContent()}</Col>
            </Row>

            <div
                className="text-center m-6-auto"
                style={{
                    marginTop: "5px",
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                }}
            >
                <button
                    type="button"
                    onClick={redirectToWynshop}
                    style={{
                        flex: 0.3,
                        height: "52px",
                        borderRadius: "30px",
                        backgroundColor: "#ffffff",
                        border: "2px solid #007932",
                        color: "#007932",
                        fontSize: "16px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                        marginBottom: "16px",
                        padding: "0 24px",
                    }}
                >
                    Go Back
                </button>
                <button
                    type="button"
                    onClick={handleLogout}
                    style={{
                        flex: 0.3,
                        height: "52px",
                        borderRadius: "30px",
                        backgroundColor: "#007932",
                        border: "2px solid #007932",
                        color: "#ffffff",
                        fontSize: "16px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                        marginBottom: "16px",
                        padding: "0 24px",
                    }}
                >
                    Log Out
                </button>
            </div>
        </div>
    );
};